// The organization pages (spec/dashboard.md section 12.4): `/orgs/:org/members`, `/orgs/:org/keys`,
// `/orgs/:org/integrations` (M7 slice 3), `/orgs/:org/audit`, tabs next to the games list. Members carries Invite… and the
// Pending invites (M7 slice 3). The controls are gated by the caller's organization role (owner / admin manage). The audit
// offers the M7 filters user, branch (an id) and environment (an id) and the `?entry=<id>` drawer (section 21.1 of the API).
import { useLocation, useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { AuditEntry, Page, ServiceKey } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../ui/Button';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { queryCache, useQuery } from '../../workspace/cache';
import { AuditPanel } from '../settings/AuditPanel';
import { AUDIT_PAGE_SIZE, ORG_ROLES } from '../settings/keys';
import { KeysPanel } from '../settings/KeysPanel';
import { MembersTable, type MemberRow } from '../settings/MembersTable';
import { useOrgAccess, type OrgAccess } from './orgAccess';
import { OrgTabs, type OrgTab } from './OrgTabs';
import { OrgIntegrations } from './Integrations';
import { InviteButton, PendingInvites } from './Invites';
import '../settings/settings.css';
import { LoadError, LoadingPane, SkeletonRows } from '../../ui/PageStates';
import { Lock, Plus } from '../../ui/icons';

export function OrgPage() {
  const { org = '' } = useParams();
  const { pathname } = useLocation();
  const { client } = useAuth();
  const orgQ = useQuery(`org:${org}`, () => client.organization(org));
  const access = useOrgAccess(org);
  const last = pathname.replace(/\/+$/, '').split('/').pop();
  const tab: OrgTab = last === 'keys' ? 'keys' : last === 'audit' ? 'audit' : last === 'integrations' ? 'integrations' : 'members';
  const name = orgQ.data?.name ?? org;
  return (
    <section data-testid="org-page" className="org-page">
      <div className="page-head">
        <h1>{name}</h1>
        {access.role && <span className="page-sub">your role: {access.role}</span>}
      </div>
      <OrgTabs org={org} value={tab} />
      {tab === 'members' && <OrgMembers org={org} access={access} />}
      {tab === 'keys' && <OrgKeys org={org} access={access} />}
      {tab === 'integrations' && <OrgIntegrationsTab org={org} access={access} />}
      {tab === 'audit' && <OrgAudit org={org} access={access} />}
    </section>
  );
}

function useWhen() {
  const [zone] = useTimeZone();
  return (v: string | null | undefined) => formatInstantWithZone(v, zone);
}

function OrgMembers({ org, access }: { org: string; access: OrgAccess }) {
  const { client } = useAuth();
  const when = useWhen();
  const key = `org:${org}:members`;
  const membersQ = useQuery(key, () => client.all((cursor) => client.data(ops.listOrganizationMembers, { path: { org }, query: { limit: 200, cursor } })));
  const reload = () => queryCache.invalidate(key);
  const path = (m: MemberRow) => ({ org, user: m.userId });
  return (
    <div data-testid="org-members" className="org-stack">
      {membersQ.error ? (
        <LoadError>The organization members could not be read.</LoadError>
      ) : null}
      <MembersTable
        caption="Organization members"
        emptyText="No members yet."
        rows={membersQ.data ?? []}
        loading={membersQ.loading}
        roles={ORG_ROLES}
        defaultRole="member"
        missing={access.missing('org_manage_members')}
        when={when}
        toolbar={<InviteButton org={org} missing={access.missing('org_manage_members')} />}
        onRoleChange={async (m, role) => {
          try {
            await client.call(ops.updateOrganizationMember, { path: path(m), body: { role }, ifMatch: Number(m.version) });
          } finally {
            reload();
          }
        }}
        onExpiryChange={async (m, expiresAt) => {
          try {
            await client.call(ops.updateOrganizationMember, { path: path(m), body: { expiresAt }, ifMatch: Number(m.version) });
          } finally {
            reload();
          }
        }}
        onRemove={async (m) => {
          await client.call(ops.removeOrganizationMember, { path: path(m) });
          reload();
        }}
        onAdd={async (email, role, expiresAt) => {
          await client.call(ops.addOrganizationMember, { path: { org }, body: expiresAt ? { email, role, expiresAt } : { email, role } });
          reload();
        }}
      />
      <PendingInvites org={org} missing={access.manages ? null : 'org_manage_members'} when={when} />
    </div>
  );
}

function OrgKeys({ org, access }: { org: string; access: OrgAccess }) {
  const { client } = useAuth();
  const when = useWhen();
  const key = `org:${org}:keys`;
  const keysQ = useQuery(access.manages ? key : null, () => client.all((cursor) => client.data(ops.listOrganizationServiceKeys, { path: { org }, query: { limit: 200, cursor } })));
  const reload = () => queryCache.invalidate(key);
  const keyPath = (k: ServiceKey) => ({ org, key: k.id });
  if (!access.manages) {
    return (
      <div data-testid="org-keys">
        <div className="pane">
          <div className="pane-head">
            <h2>Service keys</h2>
            <div className="spacer" />
            <Button variant="primary" icon={<Plus aria-hidden="true" />} missingPermission="org_manage_keys">
              Create…
            </Button>
          </div>
          <div className="pane-body">
            {access.loading ? (
              <SkeletonRows rows={2} />
            ) : (
              <p className="muted settings-locked" role="note">
                <Lock className="icon-s" aria-hidden="true" /> You lack the 'org_manage_keys' permission: the organization's service keys are visible to its owners and admins.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="org-keys">
      {keysQ.error ? (
        <LoadError>The service keys could not be read.</LoadError>
      ) : null}
      <KeysPanel
        rows={keysQ.data ?? []}
        loading={keysQ.loading}
        missing={null}
        environments={null}
        wholeLabel="the whole organization"
        when={when}
        onCreate={async (body, idempotencyKey) => {
          const r = await client.data(ops.createOrganizationServiceKey, { path: { org }, body, idempotencyKey });
          reload();
          return r;
        }}
        onRotate={async (k, graceSeconds) => {
          const r = await client.data(ops.rotateOrganizationServiceKey, { path: keyPath(k), body: { graceSeconds } });
          reload();
          return r;
        }}
        onRevoke={async (k) => {
          await client.call(ops.revokeOrganizationServiceKey, { path: keyPath(k) });
          reload();
        }}
      />
    </div>
  );
}

function OrgIntegrationsTab({ org, access }: { org: string; access: OrgAccess }) {
  const when = useWhen();
  return <OrgIntegrations org={org} access={access} when={when} />;
}

function OrgAudit({ org, access }: { org: string; access: OrgAccess }) {
  const { client } = useAuth();
  const when = useWhen();
  const gamesQ = useQuery(access.manages ? `games:${org}:all` : null, () => client.games(org, true));
  const games = gamesQ.data?.items ?? [];
  if (!access.manages) {
    return (
      <div data-testid="org-audit">
        {access.loading ? (
          <LoadingPane rows={3} />
        ) : (
          <div className="pane">
            <div className="pane-body">
              <p className="muted settings-locked" role="note">
                <Lock className="icon-s" aria-hidden="true" /> You lack the 'org_read_audit' permission.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div data-testid="org-audit">
      <AuditPanel
        cacheKey={`org:${org}:audit`}
        fetchPage={(filters, cursor) => client.data(ops.listOrganizationAudit, { path: { org }, query: { ...filters, limit: AUDIT_PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<AuditEntry>>}
        fetchEntry={(entry) => client.data(ops.getOrganizationAuditEntry, { path: { org, entry } })}
        when={when}
        games={games.map((g) => ({ slug: g.slug, name: g.archivedAt ? `${g.name} (archived)` : g.name }))}
        gameLabel={(id) => games.find((g) => g.id === id)?.slug ?? id}
      />
    </div>
  );
}
